import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = "https://bmtvendorvendorfinal.onrender.com/api/homestay-inventory";
const HOMESTAY_API = "https://bmtvendorvendorfinal.onrender.com/api/vendor/homestay";
const ROOM_API = "https://bmtvendorvendorfinal.onrender.com/api/vendor/homestay-rooms";

const getToken = () => {
  const t = localStorage.getItem("vendorToken") || localStorage.getItem("token");
  return t && t !== "null" && t !== "undefined" ? t : null;
};
const authHeaders = () => {
  const t = getToken();
  if (!t) throw new Error("Vendor token missing. Please login again.");
  return { headers: { Authorization: `Bearer ${t}` } };
};
const errMsg = (e, fallback) => e.response?.data?.message || e.message || fallback;

const EMPTY_FORM = {
  mode: "single", // single | range
  homestayId: "", roomId: "",
  date: "", startDate: "", endDate: "",
  basePrice: "", weekendPrice: "", holidayPrice: "", specialPrice: "", extraGuestPrice: "",
  discountType: "none", discountValue: "",
  taxPercentage: "", serviceChargePercentage: "",
  minimumStay: "1", maximumStay: "0",
  blockedUnits: "0", blockReason: "",
  isHoliday: false, stopSell: false, closedForBooking: false, isBlocked: false,
};

const STATUS_STYLE = {
  Available: ["#16a34a", "#dcfce7"],
  "Sold Out": ["#6b7280", "#f3f4f6"],
  "Stop Sell": ["#d97706", "#fef3c7"],
  Closed: ["#dc2626", "#fee2e2"],
  Blocked: ["#dc2626", "#fee2e2"],
};
const getStatus = (i) =>
  i.isBlocked ? "Blocked" : i.stopSell ? "Stop Sell" : i.closedForBooking ? "Closed" : i.availableUnits <= 0 ? "Sold Out" : "Available";

// dates UTC midnight par store hoti hain, isliye display bhi UTC me
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }) : "-";
const money = (n) => (n || n === 0 ? `₹${Number(n).toLocaleString("en-IN")}` : "-");

export default function VendorHomestayInventory() {
  const [view, setView] = useState("list"); // list | form
  const [homestays, setHomestays] = useState([]);
  const [roomsCache, setRoomsCache] = useState({}); // { homestayId: [rooms] }
  const [inventory, setInventory] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ homestayId: "", roomId: "", startDate: "", endDate: "" });

  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(""), 3000); };

  /* ---------- loaders ---------- */
  const loadHomestays = async () => {
    try {
      const res = await axios.get(`${HOMESTAY_API}/my?limit=100`, authHeaders());
      setHomestays(res.data.homestays || []);
    } catch (e) { setError(errMsg(e, "Homestays load nahi hue")); }
  };

  const loadRooms = useCallback(async (homestayId) => {
    if (!homestayId) return;
    try {
      const res = await axios.get(`${ROOM_API}?homestay=${homestayId}`, authHeaders());
      setRoomsCache((p) => ({ ...p, [homestayId]: res.data.rooms || [] }));
    } catch (e) { setError(errMsg(e, "Rooms load nahi hue")); }
  }, []);

  const loadInventory = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, limit: 31 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await axios.get(API, { ...authHeaders(), params });
      setInventory(res.data.inventory || []);
      setPagination(res.data.pagination || { total: 0, totalPages: 1 });
    } catch (e) {
      setInventory([]);
      setError(errMsg(e, "Failed to load inventory"));
    }
    setLoading(false);
  }, [filters, page]);

  useEffect(() => {
    if (!getToken()) { window.location.href = "/vendor-login"; return; }
    loadHomestays();
  }, []);

  useEffect(() => { loadInventory(); }, [loadInventory]);
  useEffect(() => { loadRooms(filters.homestayId); }, [filters.homestayId, loadRooms]);
  useEffect(() => { if (view === "form") loadRooms(form.homestayId); }, [view, form.homestayId, loadRooms]);

  const formRooms = roomsCache[form.homestayId] || [];
  const filterRooms = roomsCache[filters.homestayId] || [];
  const selectedRoom = useMemo(() => formRooms.find((r) => r._id === form.roomId), [formRooms, form.roomId]);

  /* ---------- filters ---------- */
  const setFilter = (k, v) => {
    setPage(1);
    setFilters((p) => ({ ...p, [k]: v, ...(k === "homestayId" ? { roomId: "" } : {}) }));
  };

  /* ---------- form helpers ---------- */
  const f = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const pickHomestay = (homestayId) => setForm((p) => ({ ...p, homestayId, roomId: "" }));

  // room select hote hi uski default prices form me aa jati hain (sirf naya inventory banate waqt)
  const pickRoom = (roomId) => {
    const room = formRooms.find((r) => r._id === roomId);
    setForm((p) => ({
      ...p,
      roomId,
      ...(editId || !room ? {} : {
        basePrice: room.pricing?.basePrice ?? "",
        weekendPrice: room.pricing?.weekendPrice || "",
        extraGuestPrice: room.pricing?.extraGuestPrice || "",
      }),
    }));
  };

  const openCreate = () => {
    setEditId(null);
    setForm({ ...EMPTY_FORM, homestayId: filters.homestayId, roomId: filters.roomId });
    setError("");
    setView("form");
    window.scrollTo({ top: 0 });
  };

  const openEdit = (item) => {
    const v = (x) => (x === undefined || x === null ? "" : String(x));
    setEditId(item._id);
    setForm({
      ...EMPTY_FORM,
      mode: "single",
      homestayId: item.homestay?._id || "",
      roomId: item.room?._id || "",
      date: item.date ? item.date.split("T")[0] : "",
      basePrice: v(item.basePrice), weekendPrice: v(item.weekendPrice), holidayPrice: v(item.holidayPrice),
      specialPrice: v(item.specialPrice), extraGuestPrice: v(item.extraGuestPrice),
      discountType: item.discountType || "none", discountValue: v(item.discountValue),
      taxPercentage: v(item.taxPercentage), serviceChargePercentage: v(item.serviceChargePercentage),
      minimumStay: v(item.minimumStay || 1), maximumStay: v(item.maximumStay || 0),
      blockedUnits: v(item.blockedUnits || 0), blockReason: item.blockReason || "",
      isHoliday: !!item.isHoliday, stopSell: !!item.stopSell,
      closedForBooking: !!item.closedForBooking, isBlocked: !!item.isBlocked,
    });
    setError("");
    setView("form");
    window.scrollTo({ top: 0 });
  };

  const closeForm = () => { setView("list"); setEditId(null); setError(""); window.scrollTo({ top: 0 }); };

  /* ---------- submit ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.homestayId) return setError("Homestay select karo");
    if (!form.roomId) return setError("Room select karo");
    if (!editId) {
      if (form.mode === "single" && !form.date) return setError("Date select karo");
      if (form.mode === "range") {
        if (!form.startDate || !form.endDate) return setError("Start aur End date select karo");
        if (form.startDate > form.endDate) return setError("Start date, End date se pehle honi chahiye");
      }
    }

    const { mode, homestayId, roomId, date, startDate, endDate, ...rest } = form;

    setSubmitting(true);
    try {
      let res;
      if (editId) {
        res = await axios.put(`${API}/${editId}`, rest, authHeaders());
      } else if (mode === "single") {
        res = await axios.post(API, { homestayId, roomId, date, ...rest }, authHeaders());
      } else {
        res = await axios.post(`${API}/range`, { homestayId, roomId, startDate, endDate, ...rest }, authHeaders());
      }
      flash(res.data?.message || "Saved!");
      closeForm();
      loadInventory();
    } catch (err) {
      setError(errMsg(err, "Failed to save inventory"));
    }
    setSubmitting(false);
  };

  /* ---------- row actions ---------- */
  const rowAction = async (fn, okMsg) => {
    try {
      const res = await fn();
      flash(res?.data?.message || okMsg);
      loadInventory();
    } catch (e) { setError(errMsg(e, "Action failed")); }
  };

  const doBlock = (id) => {
    const reason = window.prompt("Block reason (optional):", "Blocked by vendor");
    if (reason === null) return;
    rowAction(() => axios.patch(`${API}/${id}/block`, { reason }, authHeaders()), "Blocked");
  };
  const doUnblock = (id) => rowAction(() => axios.patch(`${API}/${id}/unblock`, {}, authHeaders()), "Unblocked");
  const doStop = (id) => rowAction(() => axios.patch(`${API}/${id}/stop-sell`, {}, authHeaders()), "Stop sell enabled");
  const doResume = (id) => rowAction(() => axios.patch(`${API}/${id}/resume-sell`, {}, authHeaders()), "Sell resumed");
  const doDelete = (id) => {
    if (!window.confirm("Is date ka inventory delete karna hai?")) return;
    rowAction(() => axios.delete(`${API}/${id}`, authHeaders()), "Deleted");
  };

  /* ============================================================ RENDER */
  return (
    <div style={S.page} className="hi-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        .hi-page input:focus, .hi-page select:focus, .hi-page textarea:focus { outline: none; border-color: #166534 !important; box-shadow: 0 0 0 3px rgba(22,101,52,.1) !important; }
        .hi-row:hover { background: #fafaf9; }
        @media (max-width: 800px) { .hi-grid2, .hi-grid3 { grid-template-columns: 1fr !important; } }
      `}</style>

      {success && <div style={S.toast("green")}>{success}</div>}
      {error && view === "list" && (
        <div style={S.toast("red")}>
          {error}
          <button onClick={() => setError("")} style={S.toastX}>✕</button>
        </div>
      )}

      {/* ═════════ LIST ═════════ */}
      {view === "list" && (
        <>
          <div style={S.header}>
            <div>
              <h1 style={S.title}>Room Inventory</h1>
              <p style={S.subtitle}>{pagination.total} date record(s) · rooms ki date-wise price aur availability yahan manage hoti hai</p>
            </div>
            <button onClick={openCreate} style={S.addBtn}>+ Add Inventory</button>
          </div>

          <div style={S.filterBar}>
            <select style={S.select} value={filters.homestayId} onChange={(e) => setFilter("homestayId", e.target.value)}>
              <option value="">All homestays</option>
              {homestays.map((h) => <option key={h._id} value={h._id}>{h.propertyName} — {h.city}</option>)}
            </select>
            <select style={S.select} value={filters.roomId} disabled={!filters.homestayId} onChange={(e) => setFilter("roomId", e.target.value)}>
              <option value="">{filters.homestayId ? "All rooms" : "Select homestay first"}</option>
              {filterRooms.map((r) => <option key={r._id} value={r._id}>{r.roomName}</option>)}
            </select>
            <input style={S.select} type="date" value={filters.startDate} onChange={(e) => setFilter("startDate", e.target.value)} />
            <input style={S.select} type="date" value={filters.endDate} onChange={(e) => setFilter("endDate", e.target.value)} />
            <button style={S.clearBtn} onClick={() => { setPage(1); setFilters({ homestayId: "", roomId: "", startDate: "", endDate: "" }); }}>
              Reset
            </button>
          </div>

          {loading ? (
            <div style={S.center}><div style={S.spinner} /></div>
          ) : inventory.length === 0 ? (
            <div style={S.empty}>
              <div style={{ fontSize: 56, marginBottom: 12 }}>📅</div>
              <p style={{ fontSize: 18, fontWeight: 600, color: "#374151" }}>Koi inventory nahi mili</p>
              <p style={{ color: "#9ca3af", marginTop: 8 }}>Room select karke dates open karo, tabhi user booking kar payega.</p>
              <button onClick={openCreate} style={{ ...S.addBtn, marginTop: 16 }}>+ Add Inventory</button>
            </div>
          ) : (
            <>
              <div style={S.tableWrap}>
                <table style={S.table}>
                  <thead>
                    <tr style={{ background: "#f9fafb" }}>
                      {["Date", "Room", "Homestay", "Price / night", "Units", "Status", "Actions"].map((h) => <th key={h} style={S.th}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {inventory.map((item) => {
                      const st = getStatus(item);
                      const [c, bg] = STATUS_STYLE[st];
                      const discounted = item.finalPrice < item.appliedPrice;
                      return (
                        <tr key={item._id} className="hi-row" style={S.tr}>
                          <td style={S.td}>
                            <div style={{ fontWeight: 600 }}>{fmtDate(item.date)}</div>
                            <div style={S.muted}>{item.dayType}</div>
                          </td>
                          <td style={S.td}>
                            <div style={{ fontWeight: 600 }}>{item.room?.roomName || "N/A"}</div>
                            <div style={S.muted}>{item.room?.roomType}</div>
                          </td>
                          <td style={S.td}>
                            <div>{item.homestay?.propertyName || "N/A"}</div>
                            <div style={S.muted}>{item.homestay?.city}</div>
                          </td>
                          <td style={S.td}>
                            <div style={{ fontWeight: 700, color: "#166534" }}>{money(item.finalPrice)}</div>
                            {discounted && <div style={S.muted}><s>{money(item.appliedPrice)}</s></div>}
                          </td>
                          <td style={S.td}>
                            <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                              <div>Total: {item.totalUnits}</div>
                              <div style={{ color: "#16a34a" }}>Available: {item.availableUnits}</div>
                              <div style={{ color: "#d97706" }}>Booked: {item.bookedUnits}</div>
                              {item.blockedUnits > 0 && <div style={{ color: "#dc2626" }}>Blocked: {item.blockedUnits}</div>}
                            </div>
                          </td>
                          <td style={S.td}>
                            <span style={{ ...S.badge, background: bg, color: c }}>{st}</span>
                            {item.isBlocked && item.blockReason && <div style={{ ...S.muted, marginTop: 4 }}>{item.blockReason}</div>}
                          </td>
                          <td style={S.td}>
                            <div style={{ display: "flex", gap: 4 }}>
                              <button style={S.iconBtn} title="Edit" onClick={() => openEdit(item)}>✏️</button>
                              {item.isBlocked
                                ? <button style={S.iconBtn} title="Unblock" onClick={() => doUnblock(item._id)}>🔓</button>
                                : <button style={S.iconBtn} title="Block" onClick={() => doBlock(item._id)}>🔒</button>}
                              {item.stopSell
                                ? <button style={S.iconBtn} title="Resume sell" onClick={() => doResume(item._id)}>▶️</button>
                                : <button style={S.iconBtn} title="Stop sell" onClick={() => doStop(item._id)}>⛔</button>}
                              <button style={S.iconBtn} title="Delete" onClick={() => doDelete(item._id)}>🗑️</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {pagination.totalPages > 1 && (
                <div style={S.pager}>
                  <button style={S.clearBtn} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
                  <span style={S.muted}>Page {page} of {pagination.totalPages}</span>
                  <button style={S.clearBtn} disabled={page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)}>Next →</button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ═════════ FULL PAGE FORM ═════════ */}
      {view === "form" && (
        <form onSubmit={handleSubmit}>
          <button type="button" onClick={closeForm} style={S.backLink}>← Back to inventory</button>
          <h1 style={{ ...S.title, marginTop: 8 }}>{editId ? "Edit Inventory" : "Add Inventory"}</h1>
          <p style={{ ...S.subtitle, marginBottom: 20 }}>
            Room aur dates choose karo. Khali chhodi hui prices me room ki default price lagti hai.
          </p>

          {error && <div style={S.errorBar}>{error}</div>}

          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <Card title="Homestay & Room" icon="🏠">
              <div className="hi-grid2" style={S.grid2}>
                <div>
                  <Label>Homestay *</Label>
                  <select style={S.input} value={form.homestayId} disabled={!!editId} onChange={(e) => pickHomestay(e.target.value)}>
                    <option value="">Select homestay</option>
                    {homestays.map((h) => <option key={h._id} value={h._id}>{h.propertyName} — {h.city}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Room *</Label>
                  <select style={S.input} value={form.roomId} disabled={!form.homestayId || !!editId} onChange={(e) => pickRoom(e.target.value)}>
                    <option value="">{form.homestayId ? "Select room" : "Select homestay first"}</option>
                    {formRooms.map((r) => <option key={r._id} value={r._id}>{r.roomName} ({r.totalRooms} unit{r.totalRooms > 1 ? "s" : ""})</option>)}
                  </select>
                </div>
              </div>
              {form.homestayId && roomsCache[form.homestayId] && formRooms.length === 0 && (
                <p style={{ ...S.hint, color: "#d97706" }}>Is homestay me abhi koi room nahi hai. Pehle “Units / Rooms” page se room add karo.</p>
              )}
              {selectedRoom && (
                <div style={S.roomInfo}>
                  <b>{selectedRoom.roomName}</b> · Total units: <b>{selectedRoom.totalRooms}</b> · Default price: <b>{money(selectedRoom.pricing?.basePrice)}</b>
                  {selectedRoom.pricing?.weekendPrice ? <> · Weekend: <b>{money(selectedRoom.pricing.weekendPrice)}</b></> : null}
                </div>
              )}
            </Card>

            <Card title="Dates" icon="📅">
              {editId ? (
                <div style={S.roomInfo}>Date: <b>{fmtDate(form.date)}</b> (edit me date change nahi hoti)</div>
              ) : (
                <>
                  <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                    {[["single", "Single date"], ["range", "Date range"]].map(([k, l]) => (
                      <button key={k} type="button" onClick={() => f("mode", k)}
                        style={{ ...S.tab, ...(form.mode === k ? S.tabOn : {}) }}>{l}</button>
                    ))}
                  </div>
                  {form.mode === "single" ? (
                    <div style={{ maxWidth: 320 }}>
                      <Label>Date *</Label>
                      <Input type="date" value={form.date} onChange={(v) => f("date", v)} />
                    </div>
                  ) : (
                    <div className="hi-grid2" style={S.grid2}>
                      <div><Label>Start date *</Label><Input type="date" value={form.startDate} onChange={(v) => f("startDate", v)} /></div>
                      <div><Label>End date *</Label><Input type="date" value={form.endDate} onChange={(v) => f("endDate", v)} /></div>
                    </div>
                  )}
                  {form.mode === "range" && <p style={S.hint}>Max 366 din. Jin dates par pehle se booking hai unki booked units safe rehti hain.</p>}
                </>
              )}
            </Card>

            <Card title="Pricing" icon="💰" hint="Weekend = Sat/Sun. Special price ho to wahi sabse upar lagti hai.">
              <div className="hi-grid3" style={S.grid3}>
                <div><Label>Base price (₹) *</Label><Input type="number" min="0" value={form.basePrice} onChange={(v) => f("basePrice", v)} placeholder="2500" /></div>
                <div><Label>Weekend price (₹)</Label><Input type="number" min="0" value={form.weekendPrice} onChange={(v) => f("weekendPrice", v)} placeholder="3000" /></div>
                <div><Label>Holiday price (₹)</Label><Input type="number" min="0" value={form.holidayPrice} onChange={(v) => f("holidayPrice", v)} placeholder="3500" /></div>
                <div><Label>Special price (₹)</Label><Input type="number" min="0" value={form.specialPrice} onChange={(v) => f("specialPrice", v)} placeholder="2000" /></div>
                <div><Label>Extra guest price (₹)</Label><Input type="number" min="0" value={form.extraGuestPrice} onChange={(v) => f("extraGuestPrice", v)} placeholder="500" /></div>
                <div />
                <div>
                  <Label>Discount type</Label>
                  <select style={S.input} value={form.discountType} onChange={(e) => f("discountType", e.target.value)}>
                    <option value="none">None</option>
                    <option value="percentage">Percentage</option>
                    <option value="flat">Flat (₹)</option>
                  </select>
                </div>
                <div>
                  <Label>Discount value</Label>
                  <Input type="number" min="0" value={form.discountValue} onChange={(v) => f("discountValue", v)} disabled={form.discountType === "none"} placeholder="10" />
                </div>
              </div>
              <label style={{ ...S.check, marginTop: 16 }}>
                <input type="checkbox" checked={form.isHoliday} onChange={(e) => f("isHoliday", e.target.checked)} style={S.checkbox} />
                Ye date holiday hai (holiday price lagegi)
              </label>
            </Card>

            <Card title="Tax & Stay Rules" icon="📋">
              <div className="hi-grid2" style={S.grid2}>
                <div><Label>Tax (%)</Label><Input type="number" min="0" max="100" value={form.taxPercentage} onChange={(v) => f("taxPercentage", v)} placeholder="12" /></div>
                <div><Label>Service charge (%)</Label><Input type="number" min="0" max="100" value={form.serviceChargePercentage} onChange={(v) => f("serviceChargePercentage", v)} placeholder="5" /></div>
                <div><Label>Minimum stay (nights)</Label><Input type="number" min="1" value={form.minimumStay} onChange={(v) => f("minimumStay", v)} /></div>
                <div><Label>Maximum stay (0 = unlimited)</Label><Input type="number" min="0" value={form.maximumStay} onChange={(v) => f("maximumStay", v)} /></div>
              </div>
            </Card>

            <Card title="Availability Controls" icon="🚦">
              <div className="hi-grid2" style={S.grid2}>
                <div>
                  <Label>Blocked units{selectedRoom ? ` (max ${selectedRoom.totalRooms})` : ""}</Label>
                  <Input type="number" min="0" value={form.blockedUnits} onChange={(v) => f("blockedUnits", v)} />
                </div>
                <div><Label>Block reason</Label><Input value={form.blockReason} onChange={(v) => f("blockReason", v)} placeholder="e.g., Maintenance" /></div>
              </div>
              <div style={S.checkGrid}>
                <label style={S.check}><input type="checkbox" checked={form.stopSell} onChange={(e) => f("stopSell", e.target.checked)} style={S.checkbox} /> ⛔ Stop sell</label>
                <label style={S.check}><input type="checkbox" checked={form.closedForBooking} onChange={(e) => f("closedForBooking", e.target.checked)} style={S.checkbox} /> 🔒 Closed for booking</label>
                <label style={S.check}><input type="checkbox" checked={form.isBlocked} onChange={(e) => f("isBlocked", e.target.checked)} style={S.checkbox} /> 🚫 Block whole date</label>
              </div>
            </Card>
          </div>

          <div style={S.actionBar}>
            <button type="button" onClick={closeForm} style={S.cancelBtn} disabled={submitting}>Cancel</button>
            <button type="submit" style={{ ...S.submitBtn, opacity: submitting ? 0.7 : 1 }} disabled={submitting}>
              {submitting ? "⏳ Saving..." : editId ? "💾 Update inventory" : form.mode === "range" ? "📅 Create range" : "✓ Save inventory"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ── Sub-components ── */
const Card = ({ title, icon, hint, children }) => (
  <section style={S.card}>
    <div style={{ marginBottom: 18 }}>
      <h2 style={S.cardTitle}>{icon} {title}</h2>
      {hint && <p style={{ color: "#6b7280", fontSize: 13, marginTop: 4 }}>{hint}</p>}
    </div>
    {children}
  </section>
);
const Label = ({ children }) => (
  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>{children}</label>
);
const Input = ({ onChange, ...props }) => <input {...props} onChange={(e) => onChange(e.target.value)} style={S.input} />;

/* ── Styles (Property / Rooms pages ke jaisa green theme) ── */
const S = {
  page: { maxWidth: 1200, margin: "0 auto", padding: "32px 24px 40px", fontFamily: "'DM Sans', sans-serif", background: "#f8fafc", minHeight: "100vh", color: "#111827" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 16 },
  title: { fontFamily: "'Playfair Display', serif", fontSize: 30, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 14, color: "#9ca3af", marginTop: 6 },
  backLink: { background: "none", border: "none", color: "#166534", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: 0, fontFamily: "inherit" },
  addBtn: { padding: "12px 24px", background: "#166534", color: "white", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  toast: (c) => ({ position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "14px 20px", maxWidth: 420, background: c === "green" ? "#166534" : "#dc2626", color: "white", borderRadius: 10, fontSize: 14, fontWeight: 500, boxShadow: "0 4px 20px rgba(0,0,0,.15)", display: "flex", alignItems: "center", gap: 8 }),
  toastX: { background: "none", border: "none", color: "inherit", cursor: "pointer", marginLeft: 8 },
  errorBar: { marginBottom: 16, padding: "12px 16px", background: "#fee2e2", color: "#dc2626", borderRadius: 8, fontSize: 14, fontWeight: 500 },
  filterBar: { display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "center" },
  select: { padding: "10px 14px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "white", minWidth: 170 },
  clearBtn: { padding: "10px 16px", background: "#f3f4f6", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 14, cursor: "pointer", fontFamily: "inherit" },
  center: { display: "flex", justifyContent: "center", alignItems: "center", height: 260 },
  spinner: { width: 40, height: 40, border: "3px solid #e5e7eb", borderTop: "3px solid #166534", borderRadius: "50%", animation: "spin 1s linear infinite" },
  empty: { textAlign: "center", padding: 70, background: "white", borderRadius: 16, boxShadow: "0 1px 4px rgba(0,0,0,.06)" },
  tableWrap: { background: "white", borderRadius: 16, boxShadow: "0 1px 4px rgba(0,0,0,.06)", overflow: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { padding: "14px 16px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "1px solid #f3f4f6" },
  tr: { borderBottom: "1px solid #f9fafb" },
  td: { padding: "14px 16px", fontSize: 14, color: "#374151", verticalAlign: "middle" },
  muted: { fontSize: 12, color: "#9ca3af", textTransform: "capitalize" },
  badge: { fontSize: 12, fontWeight: 600, padding: "4px 12px", borderRadius: 20, display: "inline-block" },
  iconBtn: { background: "#f3f4f6", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 15, padding: "7px 9px" },
  pager: { display: "flex", justifyContent: "center", alignItems: "center", gap: 16, marginTop: 20 },

  card: { background: "white", borderRadius: 16, padding: "24px 28px", boxShadow: "0 1px 4px rgba(0,0,0,.06)" },
  cardTitle: { fontFamily: "'Playfair Display', serif", fontSize: 19, fontWeight: 700, margin: 0 },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
  grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 },
  input: { width: "100%", padding: "10px 14px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "white", boxSizing: "border-box", transition: "border .2s, box-shadow .2s" },
  hint: { fontSize: 13, color: "#9ca3af", marginTop: 12 },
  roomInfo: { marginTop: 14, padding: "10px 14px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, fontSize: 13, color: "#166534" },
  tab: { padding: "9px 18px", border: "1.5px solid #e5e7eb", borderRadius: 20, background: "white", fontSize: 13, fontWeight: 600, color: "#6b7280", cursor: "pointer", fontFamily: "inherit" },
  tabOn: { background: "#166534", color: "white", borderColor: "#166534" },
  check: { display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#374151", cursor: "pointer" },
  checkbox: { width: 16, height: 16, accentColor: "#166534" },
  checkGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginTop: 18 },
  actionBar: { position: "sticky", bottom: 0, zIndex: 15, display: "flex", justifyContent: "flex-end", gap: 12, padding: "14px 20px", marginTop: 20, background: "rgba(255,255,255,.96)", border: "1px solid #e5e7eb", borderRadius: 14, boxShadow: "0 -2px 12px rgba(0,0,0,.06)" },
  cancelBtn: { padding: "10px 20px", background: "#f3f4f6", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", color: "#374151" },
  submitBtn: { padding: "10px 24px", background: "#166534", color: "white", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
};